<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 animate-modalFade" v-if="!isClosed">
    <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header with Countdown Timer and Close Button -->
      <div class="flex justify-between items-center">
        <!-- Countdown Timer (Moved to Left) -->
        <div v-if="timeLeft !== null" class="font-semibold text-xxs sm:text-xs bg-gray-800 bg-opacity-90 border border-gray-600 rounded p-1">
          <div class="flex items-center space-x-2 sm:space-x-3 text-center">
            <span class="px-2 py-0.5 bg-gray-600 text-gray-100 rounded">
              {{ mapName || 'ไม่ระบุแผนที่' }}
            </span>
            <span :class="isDanger ? 'text-red-600' : 'text-yellow-700'">
              เหลือเวลา: {{ formatTime(timeLeft) }}
            </span>
            <span class="text-green-700">
              <i class="fas fa-skull mr-1"></i>ซอมบี้ที่ฆ่า: {{ zombieKilled }}
            </span>
          </div>
          <p v-if="!fightResult && isDanger" class="mt-1 text-xxs text-red-700 text-center">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            คำเตือน: เหลือเวลาไม่ถึง 5 นาที ต้องรีบกลับบ้าน
          </p>
        </div>
        <!-- Close Button (with Minus Icon) -->
        <button @click="closeModal" class="text-gray-300 hover:text-red-500 focus:outline-none" title="ปิดหน้าต่าง">
          <i class="fas fa-minus text-lg"></i>
        </button>
      </div>
      <!-- Initial Loading -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center py-4">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
        <p class="text-gray-300 text-sm mt-2">กำลังโหลด...</p>
      </div>
      <!-- Opponent Section (Zombie, Merchant, Mutant, or Occupier) -->
      <div v-if="opponentData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="flex items-center gap-3 w-full">
          <img
            :src="opponentType === 'merchant' ? (opponentData?.angryFace || opponentData?.img || 'default-merchant.png') : (opponentType === 'mutant' ? (opponentData?.img || 'default-mutant.png') : (opponentData?.imageUrl || opponentData?.img || (opponentType === 'occupier' && isNPC ? 'npc-occupier.png' : 'default-zombie.png')))"
            :alt="opponentType === 'zombie' ? 'Zombie' : opponentType === 'merchant' ? 'Merchant' : opponentType === 'mutant' ? 'Mutant' : 'Occupier'"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-red-600"
            @error="handleImageError"
          />
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-red-600 text-center">
              {{ opponentData?.name || (opponentType === 'zombie' ? 'ซอมบี้' : opponentType === 'merchant' ? 'พ่อค้า' : opponentType === 'mutant' ? 'สิ่งมีชีวิตกลายพันธุ์' : (isNPC ? 'NPC ผู้ยึดครอง' : `${occupierData.characterName}`)) }}
              <span v-if="opponentType === 'merchant'">(กำลังปล้น)</span>
              <span v-if="opponentType === 'mutant'">(กำลังต่อสู้)</span>
              <span v-if="opponentType === 'occupier' && eventData.isClearing">(กำลังเคลียร์)</span>
            </h2>
            <p v-if="opponentType === 'mutant'" class="text-xs text-gray-300 text-center">จำนวนครั้งที่สามารถจับได้: {{ opponentData.catchAttempts || 0 }} / {{ opponentData.catchLimit || 1 }}</p>
            <div class="grid grid-cols-7 gap-2 text-center mt-2 bg-gray-800 bg-opacity-30 p-2 rounded-md">
              <div class="flex flex-col items-center">
                <i class="fas fa-heartbeat text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-fist-raised text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-running text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-crosshairs text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-shield-virus text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-shield-alt text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-person-rays text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full relative">
          <div class="bg-gray-500 h-5 rounded overflow-hidden">
            <div
              class="bg-red-600 h-5 rounded transition-all duration-200"
              :style="{ width: opponentType === 'merchant' || opponentType === 'occupier' ? '100%' : `${(currentOpponentHp / opponentStats.maxHp * 100) || 0}%` }"
            ></div>
            <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
              HP: {{ opponentType === 'merchant' || opponentType === 'occupier' ? '??' : currentOpponentHp }} / {{ opponentType === 'merchant' || opponentType === 'occupier' ? '??' : opponentStats.maxHp }}
            </p>
          </div>
        </div>
      </div>
      <!-- Player Section (with opponent) -->
      <div v-if="opponentData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="flex items-center gap-3 w-full">
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-blue-600 text-center">{{ characterData?.characterName || 'ผู้เล่น' }}</h2>
            <div class="grid grid-cols-7 gap-2 text-center mt-2 bg-gray-800 bg-opacity-30 p-2 rounded-md">
              <div class="flex flex-col items-center">
                <i class="fas fa-heartbeat text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-fist-raised text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-running text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-crosshairs text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-shield-virus text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-shield-alt text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-person-rays text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
          <img
            :src="characterData?.imageUrl || 'default-character.png'"
            alt="Character"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-blue-700"
            @error="handleImageError"
          />
        </div>
        <div class="w-full space-y-2">
          <!-- HP Bar -->
          <div class="relative">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-red-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>
          <!-- Infection Bar -->
          <div class="relative">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-green-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${infectionLevel || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                ติดเชื้อ: {{ infectionLevel || 0 }}%
              </p>
            </div>
          </div>
          <!-- Energy Bar -->
          <div class="relative">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-blue-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(energyLevel / maxEnergy * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                พลังงาน: {{ energyLevel || 0 }} / {{ maxEnergy }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Tabs -->
      <div v-if="!opponentData && (hasReward || hasSituation || hasMerchant || hasMutant || hasVillager || hasOccupier) && !isLoading" class="flex flex-col gap-3">
        <div class="flex border-b border-gray-600">
          <button
            v-if="hasReward"
            @click="activeTab = 'rewards'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'rewards' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            รางวัล
          </button>
          <button
            v-if="hasSituation"
            @click="activeTab = 'situations'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'situations' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            สถานการณ์
            <span v-if="situations.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ situations.length }}
            </span>
          </button>
          <button
            v-if="hasMerchant"
            @click="activeTab = 'merchants'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'merchants' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            พ่อค้า
            <span v-if="merchants.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ merchants.length }}
            </span>
          </button>
          <button
            v-if="hasMutant"
            @click="activeTab = 'mutants'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'mutants' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            สิ่งมีชีวิตกลายพันธุ์
            <span v-if="mutants.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ mutants.length }}
            </span>
          </button>
          <button
            v-if="hasVillager"
            @click="activeTab = 'villagers'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'villagers' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            ชาวบ้าน
            <span v-if="villagers.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ villagers.length }}
            </span>
          </button>
          <button
            v-if="hasOccupier"
            @click="activeTab = 'occupiers'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'occupiers' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            ผู้ยึดครอง
          </button>
        </div>
        <!-- Rewards Tab -->
        <div v-if="activeTab === 'rewards' && hasReward" class="flex flex-col gap-3">
          <div class="bg-gray-600 p-2 rounded-md flex items-center justify-between gap-2 border border-gray-600">
            <div class="flex items-center gap-3 flex-wrap">
              <div class="flex items-center gap-2">
                <span class="text-xxs sm:text-xs text-gray-300">น้ำหนัก</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="px-2 py-0.5 rounded bg-gray-700 text-gray-100 text-xxs font-semibold border border-gray-500">
                  {{ selectedWeight }} / {{ capacity }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button
                @click="toggleAllSelections"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-xxs sm:text-xs font-semibold focus:outline-none focus:ring-2"
                :class="selectedRewardIds.length
                  ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-700'
                  : 'bg-green-600 hover:bg-green-700 focus:ring-green-700'"
                :disabled="isFighting || (!rewards.length && !selectedRewardIds.length)"
                :title="selectedRewardIds.length ? 'ยกเลิกการเลือกทั้งหมด' : 'เลือกทั้งหมดแบบพอดีน้ำหนัก'">
                <i v-if="!selectedRewardIds.length" class="fas fa-check-double text-xs"></i>
                <i v-else class="fas fa-rotate-left text-xs"></i>
                <span class="hidden xs:inline">{{ selectedRewardIds.length ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด' }}</span>
                <span class="inline sm:hidden">{{ selectedRewardIds.length ? 'ยกเลิก' : 'เลือก' }}</span>
              </button>
            </div>
          </div>
          <ul class="bg-gray-600 rounded-md p-2 max-h-64 overflow-y-auto scrollbar-thin border border-gray-600">
            <li
              v-for="reward in rewards"
              :key="reward.id"
              @click="toggleRewardSelection(reward.id)"
              class="flex items-center gap-3 p-2 cursor-pointer transition-all duration-200 rounded-md mb-2 last:mb-0 border"
              :class="selectedRewardIds.includes(reward.id)
                ? 'bg-gray-700 border-l-4 border-indigo-500 '
                : 'bg-gray-700 bg-opacity-40 hover:bg-opacity-60 border-gray-600'">
              <img
                :src="reward.img || 'default-item.png'"
                alt="Reward"
                class="w-10 h-10 rounded-md object-cover border-2"
                :class="reward.rarityColors?.borderColor"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold truncate" :class="reward.rarityColors?.textColor || 'text-gray-100'">
                  {{ reward.name }} <span v-if="reward.amount">x {{ reward.amount }}</span>
                  <i v-if="reward.transfer" class="fas fa-arrow-right-arrow-left text-green-500 text-xxs ml-1"></i>
                </p>
                <p class="text-xxs text-gray-400">น้ำหนัก: {{ reward.weight || 0 }}</p>
              </div>
              <i class="fas" :class="selectedRewardIds.includes(reward.id) ? 'fa-check-circle text-green-500' : 'fa-circle text-gray-500'"></i>
            </li>
            <li v-if="!rewards.length" class="text-center text-gray-300 text-xs py-4">
              ไม่มีรางวัลให้เลือก
            </li>
          </ul>
        </div>
        <!-- Situations Tab -->
        <div v-if="activeTab === 'situations' && hasSituation" class="flex flex-col gap-3">
          <div class="bg-gray-600 rounded-md p-3 max-h-64 overflow-y-auto scrollbar-thin border border-gray-600">
            <h3 class="text-sm font-semibold text-gray-300 mb-2">สถานการณ์ที่พบ</h3>
            <div
              v-for="situation in situations"
              :key="situation.name"
              @click="selectSituation(situation.name)"
              class="flex flex-col gap-2 p-2 rounded-md mb-2 last:mb-0 cursor-pointer transition-all duration-200 border"
              :class="selectedSituationName === situation.name ? 'bg-gray-700 border-l-4 border-indigo-700 ' : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border-gray-600'">
              <div class="flex items-start gap-3">
                <i class="fas fa-exclamation-circle text-yellow-600 text-lg mt-1"></i>
                <div class="flex-1">
                  <p class="text-xs font-semibold text-gray-100">{{ situation.name }}</p>
                  <p class="text-xs text-gray-400">จำนวนซอมบี้: {{ situation.minZombies }}-{{ situation.maxZombies }} ตัว</p>
                </div>
              </div>
              <div v-if="situation.rewards?.length" class="ml-7">
                <p class="text-xs text-gray-300 font-semibold mb-1">รางวัลที่เป็นไปได้:</p>
                <ul class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <li
                    v-for="(reward, index) in situation.rewards"
                    :key="index"
                    class="flex items-center gap-2 p-1 bg-gray-700 rounded-md border border-gray-600">
                    <img
                      :src="reward.img || 'default-item.png'"
                      alt="Reward"
                      class="w-8 h-8 rounded-md object-cover border-2 border-gray-500"
                      @error="handleImageError"
                    />
                    <div>
                      <p class="text-xs font-semibold" :class="(reward.dropChance / 100).toFixed(2) + '%'">
                        {{ reward.name }}
                      </p>
                      <p class="text-xs text-gray-400">
                        โอกาส: {{ (reward.dropChance / 100).toFixed(2) }}%
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <p v-else class="ml-7 text-xs text-gray-400">ไม่มีรางวัล</p>
            </div>
            <p v-if="!situations.length" class="text-center text-gray-300 text-xs py-4">
              ไม่มีสถานการณ์ให้แสดง
            </p>
          </div>
        </div>
        <!-- Merchants Tab -->
        <div v-if="activeTab === 'merchants' && hasMerchant" class="flex flex-col gap-3">
          <div class="flex border-b border-gray-600 overflow-x-auto scrollbar-thin">
            <button
              v-for="merchant in merchants"
              :key="merchant.id"
              @click="selectedMerchantId = merchant.id"
              class="relative flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap transition duration-200 transform"
              :class="selectedMerchantId === merchant.id
                ? 'bg-gray-600 text-gray-100 border-b-2 border-yellow-500'
                : 'text-gray-300 hover:bg-gray-600 hover:-translate-y-1'"
              aria-pressed="selectedMerchantId === merchant.id">
              <img
                :src="merchant.img || 'default-merchant.png'"
                alt="Merchant"
                class="w-6 h-6 rounded-full object-cover border border-gray-500"
                @error="handleImageError"
              />
              {{ merchant.name }}
              <span v-if="selectedMerchantId === merchant.id" class="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></span>
            </button>
          </div>
          <div v-if="selectedMerchant" class="bg-gray-600 rounded-md p-3 max-h-72 overflow-y-auto scrollbar-thin border border-gray-600">
            <!-- Merchant Header -->
            <div class="flex items-center gap-3 mb-3">
              <img
                :src="selectedMerchant.img || 'default-merchant.png'"
                alt="Merchant"
                class="w-12 h-12 rounded-full object-cover border-2 border-yellow-500 shadow"
                @error="handleImageError"
              />
              <div class="flex-1">
                <div class="text-sm font-bold text-gray-100 leading-tight">{{ selectedMerchant.name }}</div>
                <div class="text-xs text-gray-400 opacity-75">{{ randomPhrase }}</div>
              </div>
            </div>
            <!-- Stats Comparison -->
            <div v-if="selectedMerchant.allowRob" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div class="bg-gray-700 rounded-md p-2 border border-gray-600">
                <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของพ่อค้า</div>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">
                      {{ Math.floor(merchantStats.maxHp * 0.1) }}-{{ merchantStats.maxHp }} / {{ merchantStats.maxHp }}
                    </p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.damageReduction }}%</p>
                  </div>
                </div>
              </div>
              <div class="bg-gray-700 rounded-md p-2 border border-gray-600">
                <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของคุณ</div>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">{{ currentHealth }} / {{ calculatedStats.maxHp }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.damageReduction }}%</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Rob Button -->
            <div v-if="selectedMerchant && selectedMerchant.allowRob" class="mt-3">
              <button
                @click="handleRobClick(selectedMerchantId)"
                class="w-full px-4 py-3 text-white text-sm rounded-md font-semibold transition duration-200 flex items-center justify-center gap-2 shadow-md"
                :class="isConfirmingRob ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                :disabled="isFighting">
                <i class="fas fa-mask text-sm"></i>
                <span class="font-bold">{{ isConfirmingRob ? 'ยืนยันปล้น' : 'ปล้น' }} {{ selectedMerchant.name }}</span>
                <span v-if="selectedMerchant.robRewards.silver" class="ml-2">{{ selectedMerchant.robRewards.silver }} <i class="fas fa-coins text-white"></i></span>
                <span v-if="selectedMerchant.robRewards.gold" class="ml-2">{{ selectedMerchant.robRewards.gold }} <i class="fas fa-g text-yellow-600"></i></span>
                <span v-if="selectedMerchant.robRewards.diamond" class="ml-2">{{ selectedMerchant.robRewards.diamond }} <i class="fas fa-gem text-blue-600"></i></span>
              </button>
            </div>
            <div class="bg-gray-700 bg-opacity-80 border border-gray-600 rounded-md px-3 py-2 my-2 shadow-sm">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 text-xs sm:text-sm text-gray-300">
                  <span class="inline-flex items-center gap-1"><i class="fas fa-coins text-white"></i>{{ characterData?.silver || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-g text-yellow-600"></i>{{ characterData?.gold || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-gem text-blue-600"></i>{{ characterData?.diamond || 0 }}</span>
                </div>
              </div>
            </div>
            <!-- Items List -->
            <transition-group name="fade-list" tag="ul" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <li
                v-for="(item, index) in selectedMerchant.items"
                :key="item.name + index"
                @click="selectedItemName = item.name"
                class="group relative p-3 bg-gray-700 rounded-lg border border-gray-600 cursor-pointer transition duration-200 transform hover:-translate-y-1 hover:shadow-md hover:border-indigo-500"
                :class="selectedItemName === item.name ? 'ring-2 ring-indigo-500 bg-gray-800' : ''">
                <div class="flex items-center gap-3">
                  <img
                    :src="item.img || 'default-item.png'"
                    alt="Item"
                    class="w-10 h-10 rounded-md object-cover border-2 flex-shrink-0"
                    :class="item.rarityColors?.borderColor || 'border-gray-500'"
                    @error="handleImageError"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="text-xs sm:text-sm" :class="item.rarityColors?.textColor || 'text-gray-100'">
                      {{ item.name }}
                      <i v-if="characterPrivateData?.learnedSkills?.includes(item.name)" class="fas fa-book text-green-500 ml-1 text-xs"></i>
                      <i v-if="item.transferable" class="fa-solid fa-arrow-right-arrow-left text-green-500 ml-1 text-xs"></i>
                    </div>
                    <div class="flex items-center gap-1 mt-1">
                      <span class="text-xs font-bold text-gray-100">{{ item.price }}</span>
                      <i v-if="item.currency === 'silver'" class="fas fa-coins text-xs text-gray-100"></i>
                      <i v-else-if="item.currency === 'gold'" class="fas fa-g text-xs text-yellow-600"></i>
                      <i v-else-if="item.currency === 'diamond'" class="fas fa-gem text-xs text-blue-600"></i>
                    </div>
                  </div>
                </div>
                <button
                  class="mt-3 w-full flex items-center justify-center gap-2 text-xs text-white rounded-md py-1.5 transition-opacity duration-200 transform translate-y-1"
                  :class="isConfirmingBuy && selectedItemToBuy === item.name ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                  :disabled="isFighting"
                  @click.stop="handleBuyClick(selectedMerchantId, item.name)"
                  title="ซื้อทันที">
                  <i class="fas fa-cart-plus text-xs"></i>
                  {{ isConfirmingBuy && selectedItemToBuy === item.name ? 'ยืนยันซื้อ' : 'ซื้อทันที' }}
                </button>
              </li>
            </transition-group>
          </div>
          <p v-if="!merchants.length" class="text-center text-gray-300 text-xs py-4">
            ไม่มีพ่อค้าให้แสดง
          </p>
        </div>
        <!-- Mutants Tab -->
        <div v-if="activeTab === 'mutants' && hasMutant" class="flex flex-col gap-3">
          <div class="flex border-b border-gray-600 overflow-x-auto scrollbar-thin">
            <button
              v-for="mutant in mutants"
              :key="mutant.id"
              @click="selectedMutantId = mutant.id"
              class="relative flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap transition duration-200 transform"
              :class="selectedMutantId === mutant.id
                ? 'bg-gray-600 text-gray-100 border-b-2 border-yellow-500'
                : 'text-gray-300 hover:bg-gray-600 hover:-translate-y-1'"
              aria-pressed="selectedMutantId === mutant.id">
              <img
                :src="mutant.img || 'default-mutant.png'"
                alt="Mutant"
                class="w-6 h-6 rounded-full object-cover border border-gray-500"
                @error="handleImageError"
              />
              {{ mutant.name }}
              <span v-if="selectedMutantId === mutant.id" class="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></span>
            </button>
          </div>
          <div v-if="selectedMutant" class="bg-gray-600 rounded-md p-3 max-h-72 overflow-y-auto scrollbar-thin border border-gray-600">
            <!-- Mutant Header -->
            <div class="flex items-center gap-3 mb-3">
              <img
                :src="selectedMutant.img || 'default-mutant.png'"
                alt="Mutant"
                class="w-12 h-12 rounded-full object-cover border-2 border-yellow-500 shadow"
                @error="handleImageError"
              />
              <div class="flex-1">
                <div class="text-sm font-bold text-gray-100 leading-tight">{{ selectedMutant.name }}</div>
                <p class="text-xs text-gray-300">จำนวนครั้งที่จับ: {{ selectedMutant.catchAttempts || 0 }} / {{ selectedMutant.catchLimit || 1 }}</p>
                <p class="text-xs text-gray-300">ความยากในการจับ: {{ selectedMutant.catchDifficulty || 1 }}</p>
                <div class="text-xs text-gray-400 opacity-75">{{ mutantPhrase }}</div>
              </div>
            </div>
            <!-- Stats Comparison -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div class="bg-gray-700 rounded-md p-2 border border-gray-600">
                <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของสิ่งมีชีวิตกลายพันธุ์</div>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">
                     {{ mutantStats.maxHp }} / {{ mutantStats.maxHp }}
                    </p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ mutantStats.atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ mutantStats.evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ mutantStats.critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ mutantStats.criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ mutantStats.defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ mutantStats.damageReduction }}%</p>
                  </div>
                </div>
              </div>
              <div class="bg-gray-700 rounded-md p-2 border border-gray-600">
                <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของคุณ</div>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">{{ currentHealth }} / {{ calculatedStats.maxHp }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.damageReduction }}%</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Fight Button -->
            <div class="mt-3">
              <button
                @click="handleFightClick(selectedMutantId)"
                class="w-full px-4 py-3 text-white text-sm rounded-md font-semibold transition duration-200 flex items-center justify-center gap-2 shadow-md"
                :class="isConfirmingFight ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                :disabled="isFighting">
                <i class="fas fa-fist-raised text-sm"></i>
                <span class="font-bold">{{ isConfirmingFight ? 'ยืนยันต่อสู้' : 'ต่อสู้' }} {{ selectedMutant.name }}</span>
              </button>
            </div>
            <div class="bg-gray-700 bg-opacity-80 border border-gray-600 rounded-md px-3 py-2 my-2 shadow-sm">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 text-xs sm:text-sm text-gray-300">
                  <span class="inline-flex items-center gap-1"><i class="fas fa-coins text-white"></i>{{ characterData?.silver || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-g text-yellow-600"></i>{{ characterData?.gold || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-gem text-blue-600"></i>{{ characterData?.diamond || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
          <p v-if="!mutants.length" class="text-center text-gray-300 text-xs py-4">
            ไม่มีสิ่งมีชีวิตกลายพันธุ์ให้แสดง
          </p>
        </div>
        <!-- Villagers Tab -->
        <div v-if="activeTab === 'villagers' && hasVillager" class="flex flex-col gap-3">
          <div class="flex border-b border-gray-600 overflow-x-auto scrollbar-thin">
            <button
              v-for="villager in villagers"
              :key="villager.id"
              @click="selectedVillagerId = villager.id"
              class="relative flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap transition duration-200 transform"
              :class="selectedVillagerId === villager.id
                ? 'bg-gray-600 text-gray-100 border-b-2 border-yellow-500'
                : 'text-gray-300 hover:bg-gray-600 hover:-translate-y-1'"
              aria-pressed="selectedVillagerId === villager.id">
              <img
                :src="villager.img || 'default-villager.png'"
                alt="Villager"
                class="w-6 h-6 rounded-full object-cover border border-gray-500"
                @error="handleImageError"
              />
              {{ villager.name }}
              <span v-if="selectedVillagerId === villager.id" class="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></span>
            </button>
          </div>
          <div v-if="selectedVillager" class="bg-gray-600 rounded-md p-3 max-h-72 overflow-y-auto scrollbar-thin border border-gray-600">
            <!-- Villager Header -->
            <div class="flex items-center gap-3 mb-3">
              <img
                :src="selectedVillager.normalFace || selectedVillager.img || 'default-villager.png'"
                alt="Villager"
                class="w-12 h-12 rounded-full object-cover border-2 border-yellow-500 shadow"
                @error="handleImageError"
              />
              <div class="flex-1">
                <div class="text-sm font-bold text-gray-100 leading-tight">{{ selectedVillager.name }}</div>
                <div class="flex items-center space-x-1 mt-1">
                  <!-- ชิปบุก -->
                  <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                    <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                    <span class="font-mono">6</span>
                  </div>
                  <!-- ชิปตั้งรับ -->
                  <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                    <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                    <span class="font-mono">6</span>
                  </div>
                </div>
                <div class="text-xs text-gray-400 opacity-75">{{ villagerPhrase }}</div>
              </div>
            </div>
            <!-- Hire Cost -->
            <div class="bg-gray-700 rounded-md p-2 border border-gray-600 mb-3">
              <div class="text-center text-gray-300 mb-2 font-semibold text-xs">ค่าใช้จ่ายในการจ้าง</div>
              <div class="grid grid-cols-3 gap-2 text-center text-gray-100">
                <div class="flex flex-col items-center">
                  <i class="fas fa-coins text-white"></i>
                  <p class="text-xs font-bold mt-1">{{ selectedVillager.hireCosts?.silver || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-g text-yellow-600"></i>
                  <p class="text-xs font-bold mt-1">{{ selectedVillager.hireCosts?.gold || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-gem text-blue-600"></i>
                  <p class="text-xs font-bold mt-1">{{ selectedVillager.hireCosts?.diamond || 0 }}</p>
                </div>
              </div>
            </div>
            <!-- Base Stats (Uncalculated) -->
            <div class="bg-gray-700 rounded-md p-2 border border-gray-600 mb-3">
              <div class="text-center text-gray-300 mb-2 font-semibold text-xs">ค่าสถานะพื้นฐาน (ยังไม่คำนวณ)</div>
              <div class="grid grid-cols-3 gap-2 text-center text-gray-100">
                <div class="flex flex-col items-center">
                  <i class="fas fa-heartbeat text-red-600"></i>
                  <p class="text-xs font-bold mt-1">Sta: {{ selectedVillager.stats?.stamina || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-fist-raised text-orange-600"></i>
                  <p class="text-xs font-bold mt-1">Str: {{ selectedVillager.stats?.str || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-brain text-purple-600"></i>
                  <p class="text-xs font-bold mt-1">Int: {{ selectedVillager.stats?.int || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-running text-green-600"></i>
                  <p class="text-xs font-bold mt-1">Agi: {{ selectedVillager.stats?.agi || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-shield-alt text-blue-600"></i>
                  <p class="text-xs font-bold mt-1">Def: {{ selectedVillager.stats?.defense || 0 }}</p>
                </div>
                <div class="flex flex-col items-center">
                  <i class="fas fa-clover text-green-600"></i>
                  <p class="text-xs font-bold mt-1">Luck: {{ selectedVillager.stats?.luck || 0 }}</p>
                </div>
              </div>
            </div>
            <!-- Calculated Stats -->
            <div class="bg-gray-700 rounded-md p-2 border border-gray-600 mb-3">
              <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของชาวบ้าน (คำนวณแล้ว)</div>
              <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fas fa-heartbeat text-red-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.maxHp }}</p>
                </div>
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fas fa-fist-raised text-orange-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.atk }}</p>
                </div>
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fas fa-running text-green-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.evasion }}%</p>
                </div>
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fas fa-crosshairs text-yellow-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.critical }}%</p>
                </div>
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fa-solid fa-shield-virus text-purple-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.criticalReduction }}%</p>
                </div>
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fas fa-shield-alt text-blue-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.defense }}</p>
                </div>
                <div class="flex flex-col items-center text-gray-100">
                  <i class="fa-solid fa-person-rays text-indigo-600"></i>
                  <p class="text-xs font-bold mt-1">{{ villagerStats.damageReduction }}%</p>
                </div>
              </div>
            </div>
            <!-- Weapon -->
            <div class="bg-gray-700 rounded-md p-2 border border-gray-600 mb-3">
              <div class="text-center text-gray-300 mb-2 font-semibold text-xs">อาวุธที่ใช้</div>
              <div class="text-center text-xs text-gray-100">{{ selectedVillager.weapon || 'ไม่มีอาวุธ' }}</div>
            </div>
            <!-- Strong / Weak -->
            <div class="bg-gray-700 rounded-md p-2 border border-gray-600 mb-3">
              <div class="text-center text-gray-300 mb-2 font-semibold text-xs">อาวุธที่แพ้–ชนะ</div>
              <p class="text-xs text-gray-300 mb-1 text-center">อาวุธที่ชนะทาง</p>
              <div class="flex flex-wrap justify-center gap-2 mb-3">
                <span v-for="w in selectedVillager.strongAgainst || []" :key="'s'+w"
                  class="px-2 py-1 text-xs rounded-full bg-green-700 border border-green-500 text-green-100">{{ w }}</span>
                <span v-if="!(selectedVillager.strongAgainst && selectedVillager.strongAgainst.length)" class="text-xs text-gray-300">— ไม่มีข้อมูล —</span>
              </div>
              <p class="text-xs text-gray-300 mb-1 text-center">อาวุธที่แพ้ทาง</p>
              <div class="flex flex-wrap justify-center gap-2">
                <span v-for="w in selectedVillager.weakAgainst || []" :key="'w'+w"
                  class="px-2 py-1 text-xs rounded-full bg-red-700 border border-red-500 text-red-100">{{ w }}</span>
                <span v-if="!(selectedVillager.weakAgainst && selectedVillager.weakAgainst.length)" class="text-xs text-gray-300">— ไม่มีข้อมูล —</span>
              </div>
            </div>
            <!-- Perks -->
            <div v-if="selectedVillager.perks?.length" class="bg-gray-700 rounded-md p-2 border border-gray-600 mb-3">
              <div class="text-center text-gray-300 mb-2 font-semibold text-xs">Perks</div>
              <ul class="space-y-2 text-xs text-gray-100">
                <li v-for="p in selectedVillager.perks" :key="p">
                  <strong>{{ p }}</strong> - {{ getPerkDescription(p) }}
                </li>
              </ul>
            </div>
            <!-- Hire Button -->
            <div class="mt-3">
              <button
                @click="handleHireClick(selectedVillagerId)"
                class="w-full px-4 py-3 text-white text-sm rounded-md font-semibold transition duration-200 flex items-center justify-center gap-2 shadow-md"
                :class="isConfirmingHire ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                :disabled="isFighting">
                <i class="fas fa-handshake text-sm"></i>
                <span class="font-bold">{{ isConfirmingHire ? 'ยืนยันจ้าง' : 'จ้าง' }} {{ selectedVillager.name }}</span>
                <span v-if="selectedVillager.hireCosts.silver" class="ml-2">{{ selectedVillager.hireCosts.silver }} <i class="fas fa-coins text-white"></i></span>
                <span v-if="selectedVillager.hireCosts.gold" class="ml-2">{{ selectedVillager.hireCosts.gold }} <i class="fas fa-g text-yellow-600"></i></span>
                <span v-if="selectedVillager.hireCosts.diamond" class="ml-2">{{ selectedVillager.hireCosts.diamond }} <i class="fas fa-gem text-blue-600"></i></span>
              </button>
            </div>
            <div class="bg-gray-700 bg-opacity-80 border border-gray-600 rounded-md px-3 py-2 my-2 shadow-sm">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 text-xs sm:text-sm text-gray-300">
                  <span class="inline-flex items-center gap-1"><i class="fas fa-coins text-white"></i>{{ characterData?.silver || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-g text-yellow-600"></i>{{ characterData?.gold || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-gem text-blue-600"></i>{{ characterData?.diamond || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
          <p v-if="!villagers.length" class="text-center text-gray-300 text-xs py-4">
            ไม่มีชาวบ้านให้แสดง
          </p>
        </div>
        <!-- Occupiers Tab -->
        <div v-if="activeTab === 'occupiers' && hasOccupier" class="flex flex-col gap-3">
          <div class="bg-gray-700 bg-opacity-80 rounded-md p-3 border border-gray-600">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-sm font-semibold text-indigo-200 flex items-center gap-2">
                <i class="fas fa-flag text-indigo-400"></i>
                ผู้ยึดครองแผนที่
              </h3>
              <div v-if="eventData?.isClearing" class="px-2 py-0.5 rounded-md text-xxs font-semibold bg-red-700/30 text-red-300 border border-red-700">
                กำลังเคลียร์
              </div>
            </div>
            <!-- Occupier Card -->
            <div v-if="occupierData" class="group relative p-3 rounded-md bg-gray-800 border border-gray-700 hover:border-indigo-500 transition duration-200">
              <div class="flex items-start gap-3">
                <img
                  :src="isNPC
                    ? 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/merchant%2F%E0%B8%9E%E0%B8%A3%E0%B8%B0%E0%B8%9A%E0%B8%A3%E0%B8%A1%E0%B8%A1%E0%B8%AB%E0%B8%B2%E0%B8%A3%E0%B8%B2%E0%B8%8A%E0%B8%A7%E0%B8%B1%E0%B8%87%2F%E0%B8%A3%E0%B8%B4%E0%B8%95%E0%B8%A0%E0%B8%9E.png?alt=media&token=c2f45d24-2cd6-485d-bfc6-bc6ade04e0c4'
                    : (occupierData.imageUrl || 'default-character.png')"
                  alt="Occupier"
                  class="w-16 h-16 rounded-md object-cover border-2"
                  :class="isNPC ? 'border-amber-600' : 'border-red-600'"
                  @error="handleImageError"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm sm:text-base font-semibold"
                       :class="isNPC ? 'text-amber-300' : 'text-red-400'">
                      {{ occupierDisplayName }}
                    </p>
                    <span
                      class="text-xxs px-2 py-0.5 rounded-md border"
                      :class="isNPC
                        ? 'bg-amber-700/30 text-amber-300 border-amber-700'
                        : 'bg-red-700/30 text-red-300 border-red-700'">
                      {{ isNPC ? 'NPC' : 'ผู้เล่น' }}
                    </span>
                  </div>
                  <div class="mt-2 text-xxs text-gray-400 bg-gray-900/60 border border-gray-700 rounded p-2">
                    <i class="fas fa-info-circle mr-1 text-indigo-400"></i>
                    การต่อสู้เป็นการจำลองพลังของผู้ยึดครองจริง เมื่อตายคุณจะไม่สูญเสียอะไรเลยแต่เลือดจะเหลือ 1 หน่วย
                  </div>
                </div>
              </div>
              <!-- Actions -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                <button
                  @click="handleChallengeClick"
                  class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition duration-200 flex items-center justify-center gap-2 group shadow-sm
                         border border-indigo-700 bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-0.5"
                  :disabled="isFighting || isConfirmingClear">
                  <i class="fas fa-sword text-xs group-hover:animate-pulse"></i>
                  {{ isConfirmingChallenge ? 'ยืนยันท้าประลอง' : 'ท้าประลอง' }}
                </button>
                <button
                  v-if="!isNPC"
                  @click="handleClearClick"
                  class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition duration-200 flex items-center justify-center gap-2 group shadow-sm
                         border border-orange-700"
                  :class="isConfirmingClear
                    ? 'bg-red-600 hover:bg-red-700 hover:-translate-y-0.5'
                    : 'bg-orange-600 hover:bg-orange-700 hover:-translate-y-0.5'"
                  :disabled="isFighting || isConfirmingChallenge">
                  <i class="fas fa-broom text-xs group-hover:animate-pulse"></i>
                  {{ isConfirmingClear ? 'ยืนยันเคลียร์' : 'เคลียร์ผู้ยึดครอง' }}
                </button>
              </div>
              <!-- Decorative underline -->
              <span class="absolute left-0 right-0 -bottom-px h-px bg-gradient-to-r from-transparent via-indigo-600 to-transparent opacity-40"></span>
            </div>
            <!-- Empty state -->
            <p v-else class="text-center text-gray-300 text-xs py-6 bg-gray-800 border border-gray-700 rounded-md">
              ไม่มีข้อมูลผู้ยึดครอง
            </p>
          </div>
        </div>
      </div>
      <!-- Player Section (Bars only when no opponent) -->
      <div v-if="!opponentData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-750 bg-opacity-80 border border-gray-700 rounded-md ">
        <div class="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <!-- HP Bar -->
          <div class="relative flex-1">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-red-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>
          <!-- Infection Bar -->
          <div class="relative flex-1">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-green-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${infectionLevel || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                ติดเชื้อ: {{ infectionLevel || 0 }}%
              </p>
            </div>
          </div>
          <!-- Energy Bar -->
          <div class="relative flex-1">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-blue-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(energyLevel / maxEnergy * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                พลังงาน: {{ energyLevel || 0 }} / {{ maxEnergy }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Action Buttons -->
      <div class="flex flex-col gap-2">
        <button
          v-if="opponentData"
          @click="attackOpponentHandler()"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || (opponentType === 'zombie' && energyLevel <= 0) || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-skull-crossbones text-xs group-hover:animate-pulse"></i>
          {{ opponentType === 'occupier' ? (eventData.isClearing ? 'โจมตีผู้ยึดครอง' : 'โจมตีผู้ยึดครอง') : 'โจมตี' }}
        </button>
        <div v-if="opponentData" class="flex gap-2">
          <button
            @click="toggleAutoAttack(250)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 250 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || (opponentType === 'zombie' && energyLevel <= 0) || !opponentData || infectionLevel >= 95)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 250" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 250 ? '(0.25s)' : '(0.25s)' }}
          </button>
          <button
            @click="toggleAutoAttack(500)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 500 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || (opponentType === 'zombie' && energyLevel <= 0) || !opponentData || infectionLevel >= 95)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 500" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 500 ? '(0.5s)' : '(0.5s)' }}
          </button>
          <button
            @click="toggleAutoAttack(1000)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 1000 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || (opponentType === 'zombie' && energyLevel <= 0) || !opponentData || infectionLevel >= 95)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 1000" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 1000 ? '(1s)' : '(1s)' }}
          </button>
        </div>
        <button
          v-if="opponentData && opponentType === 'mutant' && currentOpponentHp < opponentStats.maxHp * 0.5 && opponentData.canCatch"
          @click="handleCaptureClick"
          class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :class="isConfirmingCapture ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-hand-sparkles text-xs group-hover:animate-pulse"></i>
          {{ isConfirmingCapture ? 'ยืนยันจับ' : 'จับสิ่งมีชีวิตกลายพันธุ์' }} ({{ opponentData.catchAttempts || 0 }} / {{ opponentData.catchLimit || 1 }})
        </button>
        <button
          v-if="opponentData && !showFleeOptions"
          @click="showFleeOptions = true"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          หนี
        </button>
        <button
          v-if="opponentData && showFleeOptions"
          @click="fleeOpponentHandler()"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          {{ opponentType === 'occupier' ? (eventData.isClearing ? 'หนีการเคลียร์' : 'หนีผู้ยึดครอง') : 'หนี' }}
          {{ opponentType === 'occupier' ? '(โอกาสสำเร็จ 100%)' : `(โอกาสสำเร็จ ${fleeChance}%)` }}
        </button>
        <button
          v-if="opponentData && showFleeOptions && eventData.escapeAttempts > 0 && opponentType !== 'merchant' && opponentType !== 'mutant' && opponentType !== 'occupier'"
          @click="fleeBySilver"
          class="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          หนี {{ eventData.escapeCoins }} เหรียญ (โอกาสสำเร็จ 60%)
        </button>
        <button
  v-if="selectedRewardIds.length > 0 && activeTab === 'rewards'"
  @click="addToBag"
  class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
  :disabled="isFighting || isAddingToBag || selectedWeight > maxLoad - currentWeight">
  <div v-if="isAddingToBag" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
  <i v-else class="fas fa-shopping-bag text-xs group-hover:animate-pulse"></i>
  {{ isAddingToBag ? 'กำลังนำใส่กระเป๋า...' : `นำใส่กระเป๋า (${selectedRewardIds.length} รายการ)` }}
</button>
        <button
          v-if="!opponentData && hasSituation && activeTab === 'situations' && selectedSituationName"
          @click="continueExploring"
          class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-map text-xs group-hover:animate-pulse"></i>
          สำรวจต่อ
        </button>
        <button
          v-if="!opponentData"
          @click="handleGoHomeClick"
          class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :class="isConfirmingGoHome ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'"
          :disabled="isFighting || !canGoHome">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-home text-xs group-hover:animate-bounce"></i>
          {{ isConfirmingGoHome ? 'ยืนยันกลับบ้าน' : 'กลับบ้าน' }}
        </button>
      </div>
      <!-- Fight Result -->
      <p v-if="fightResult" class="text-xs text-gray-300 text-center">{{ fightResult }}</p>
    </div>
  </div>
</template>
<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { useInventoryStore } from '@/composables/useInventoryStore';
import { useMissionStatsStore } from '@/composables/useMissionStatsStore';
import { usePlayerStore } from '@/composables/usePlayerStore';
import gameItemsData from '@/data/gameItems.json';
export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  emits: ['fightCompleted', 'close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const inventoryStore = useInventoryStore();
    const playerStore = usePlayerStore();
    // Initialize callables
    const gameActions = projectFunctions.httpsCallable('gameActions');
    // ===== Reactive States =====
    const isLoading = ref(true);
    const zombieData = ref(null);
    const merchantData = ref(null);
    const mutantData = ref(null);
    const villagerData = ref(null);
    const occupierData = ref(null);
    const isNPC = ref(false);
    const characterData = computed(() => playerStore.characterData || {});
    const characterPrivateData = computed(() => playerStore.characterPrivateData || {});
    const currentOpponentHp = ref(0);
    const fightResult = ref('');
    const isFighting = ref(false);
    const hasReward = ref(false);
    const hasSituation = ref(false);
    const hasMerchant = ref(false);
    const hasMutant = ref(false);
    const hasVillager = ref(false);
    const hasOccupier = ref(false);
    const encounterOccupier = ref(false);
    const isChallenging = ref(false);
    const situations = ref([]);
    const selectedSituationName = ref(null);
    const zombieKilled = ref(0);
    const rewards = ref([]);
    const selectedRewardIds = ref([]);
    const merchants = ref([]);
    const mutants = ref([]);
    const villagers = ref([]);
    const selectedMerchantId = ref(null);
    const selectedMutantId = ref(null);
    const selectedVillagerId = ref(null);
    const selectedItemName = ref(null);
    const expiresAt = ref(null);
    const timeLeft = ref(null);
    const isDanger = computed(() => (timeLeft.value ?? 0) <= 300000);
    const energyLevel = computed(() => playerStore.characterPrivateData?.energyLevel || 0);
    const maxEnergy = ref(4800);
    const currentWeight = ref(0);
    const maxLoad = ref(0);
    const isAutoAttacking = ref(false);
    const autoAttackIntervalMs = ref(null);
    const eventData = ref({ escapeCoins: 0, escapeAttempts: 0, diamondReward: 0, challengeCostDiamonds: 0, isClearing: false });
    const showFleeOptions = ref(false);
    const isConfirmingGoHome = ref(false);
    const isConfirmingChallenge = ref(false);
    const isConfirmingClear = ref(false);
    const activeTab = ref('rewards');
    const mapName = ref('');
    const currentMerchantId = ref(null);
    const canGoHome = ref(true);
    const isConfirmingRob = ref(false);
    const isConfirmingFight = ref(false);
    const isConfirmingCapture = ref(false);
    const isClosed = ref(false);
    const isConfirmingBuy = ref(false);
    const selectedItemToBuy = ref(null);
    const isConfirmingHire = ref(false);
    const isAddingToBag = ref(false);
    // ===== Timers & Unsubscribes =====
    let timer = null;
    let confirmTimeout = null;
    let confirmRobTimeout = null;
    let confirmFightTimeout = null;
    let confirmCaptureTimeout = null;
    let confirmChallengeTimeout = null;
    let confirmClearTimeout = null;
    let confirmBuyTimeout = null;
    let confirmHireTimeout = null;
    let goHomeUnlockTimeout = null;
    let unsubscribeEvent = null;
    let unsubscribeZombie = null;
    let unsubscribeMerchant = null;
    let unsubscribeMutant = null;
    let unsubscribeVillager = null;
    let unsubscribeMerchants = ref([]);
    let unsubscribeMutants = ref([]);
    let unsubscribeVillagers = ref([]);
    const resetEmbeddedEventSubscriptions = () => {
      if (unsubscribeEvent) {
        unsubscribeEvent();
        unsubscribeEvent = null;
      }
      if (unsubscribeZombie) {
        unsubscribeZombie();
        unsubscribeZombie = null;
      }
      if (unsubscribeMerchant) {
        unsubscribeMerchant();
        unsubscribeMerchant = null;
      }
      if (unsubscribeMutant) {
        unsubscribeMutant();
        unsubscribeMutant = null;
      }
      if (unsubscribeVillager) {
        unsubscribeVillager();
        unsubscribeVillager = null;
      }
      unsubscribeMerchants.value.forEach((unsub) => unsub());
      unsubscribeMutants.value.forEach((unsub) => unsub());
      unsubscribeVillagers.value.forEach((unsub) => unsub());
      unsubscribeMerchants.value = [];
      unsubscribeMutants.value = [];
      unsubscribeVillagers.value = [];
    };
    const resetEmbeddedEventState = () => {
      if (goHomeUnlockTimeout) {
        clearTimeout(goHomeUnlockTimeout);
        goHomeUnlockTimeout = null;
      }
      eventData.value = { escapeCoins: 0, escapeAttempts: 0, diamondReward: 0, challengeCostDiamonds: 0, isClearing: false };
      zombieData.value = null;
      merchantData.value = null;
      mutantData.value = null;
      villagerData.value = null;
      occupierData.value = null;
      currentOpponentHp.value = 0;
      rewards.value = [];
      merchants.value = [];
      mutants.value = [];
      villagers.value = [];
      situations.value = [];
      selectedRewardIds.value = [];
      selectedSituationName.value = null;
      selectedMerchantId.value = null;
      selectedMutantId.value = null;
      selectedVillagerId.value = null;
      currentMerchantId.value = null;
      hasReward.value = false;
      hasSituation.value = false;
      hasMerchant.value = false;
      hasMutant.value = false;
      hasVillager.value = false;
      hasOccupier.value = false;
      encounterOccupier.value = false;
      isChallenging.value = false;
      mapName.value = '';
      canGoHome.value = true;
    };
    const syncGoHomeAvailability = () => {
      canGoHome.value =  !goHomeUnlockTimeout;
    };
    const lockGoHomeTemporarily = (duration = 2000) => {
  canGoHome.value = false;
  if (goHomeUnlockTimeout) {
    clearTimeout(goHomeUnlockTimeout);
  }
  goHomeUnlockTimeout = setTimeout(() => {
    goHomeUnlockTimeout = null;
    canGoHome.value = true;
  }, duration);
};
    // ===== Auto-Attack (10s grace when switching zombies) =====
    let autoAttackInterval = null;
    let noZombieSince = null;
    const NO_ZOMBIE_GRACE_MS = 30000;
    // ===== Computed =====
    const opponentType = computed(() => {
      if (zombieData.value) return 'zombie';
      if (merchantData.value) return 'merchant';
      if (mutantData.value) return 'mutant';
      if (villagerData.value) return 'villager';
      if (isChallenging.value && occupierData.value) return 'occupier';
      return null;
    });
    const opponentData = computed(() => zombieData.value || merchantData.value || mutantData.value || villagerData.value || (isChallenging.value ? occupierData.value : null));
    const opponentStats = computed(() => {
      if (opponentType.value === 'occupier') {
        return { maxHp: '??', atk: '??', critical: '??', evasion: '??', criticalReduction: '??', defense: '??', damageReduction: '??', flee: '??' };
      }
      if (!opponentData.value) {
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0, flee: 0 };
      }
      const stats = opponentData.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor(((stats.int || 0) / 2) + (stats.critical || 0)),
        evasion: Math.floor(((stats.agi || 0) / 2) + (stats.evasion || 0)),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
        flee: Math.floor((((stats.agi || 0) / 2) + ((stats.int || 0) / 2)) / 2),
      };
    });
    const selectedMerchant = computed(() =>
      merchants.value.find((m) => m.id === selectedMerchantId.value) || null
    );
    const selectedMutant = computed(() =>
      mutants.value.find((m) => m.id === selectedMutantId.value) || null
    );
    const selectedVillager = computed(() =>
      villagers.value.find((v) => v.id === selectedVillagerId.value) || null
    );
    const calculatedStats = computed(() => {
      if (!characterPrivateData.value) {
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0, flee: 0 };
      }
      const stats = characterPrivateData.value;
      const str =
        (stats.itemStats?.str || 0) +
        (stats.bonusStats?.str || 0) +
        (stats.stats?.str || 0) +
        (stats.traitStats?.str || 0) +
        (stats.skillStats?.str || 0) +
        (stats.titleStats?.str || 0) +
        (stats.tattooStats?.str || 0) +
        (stats.ghostStats?.str || 0);
      const stamina =
        (stats.itemStats?.stamina || 0) +
        (stats.bonusStats?.stamina || 0) +
        (stats.stats?.stamina || 0) +
        (stats.traitStats?.stamina || 0) +
        (stats.skillStats?.stamina || 0) +
        (stats.titleStats?.stamina || 0) +
        (stats.tattooStats?.stamina || 0) +
        (stats.ghostStats?.stamina || 0);
      const int =
        (stats.itemStats?.int || 0) +
        (stats.bonusStats?.int || 0) +
        (stats.stats?.int || 0) +
        (stats.traitStats?.int || 0) +
        (stats.skillStats?.int || 0) +
        (stats.titleStats?.int || 0) +
        (stats.tattooStats?.int || 0) +
        (stats.ghostStats?.int || 0);
      const agi =
        (stats.itemStats?.agi || 0) +
        (stats.bonusStats?.agi || 0) +
        (stats.stats?.agi || 0) +
        (stats.traitStats?.agi || 0) +
        (stats.skillStats?.agi || 0) +
        (stats.titleStats?.agi || 0) +
        (stats.tattooStats?.agi || 0) +
        (stats.ghostStats?.agi || 0);
      const luck =
        (stats.itemStats?.luck || 0) +
        (stats.bonusStats?.luck || 0) +
        (stats.stats?.luck || 0) +
        (stats.traitStats?.luck || 0) +
        (stats.skillStats?.luck || 0) +
        (stats.titleStats?.luck || 0) +
        (stats.tattooStats?.luck || 0) +
        (stats.ghostStats?.luck || 0);
      const defense =
        (stats.itemStats?.defense || 0) +
        (stats.bonusStats?.defense || 0) +
        (stats.stats?.defense || 0) +
        (stats.traitStats?.defense || 0) +
        (stats.skillStats?.defense || 0) +
        (stats.titleStats?.defense || 0) +
        (stats.tattooStats?.defense || 0) +
        (stats.ghostStats?.defense || 0);
      const flatMaxHp =
        (stats.itemStats?.maxHp || 0) +
        (stats.bonusStats?.maxHp || 0) +
        (stats.stats?.maxHp || 0) +
        (stats.traitStats?.maxHp || 0) +
        (stats.skillStats?.maxHp || 0) +
        (stats.titleStats?.maxHp || 0) +
        (stats.tattooStats?.maxHp || 0) +
        (stats.ghostStats?.maxHp || 0);
      const flatAtk =
        (stats.itemStats?.atk || 0) +
        (stats.bonusStats?.atk || 0) +
        (stats.stats?.atk || 0) +
        (stats.traitStats?.atk || 0) +
        (stats.skillStats?.atk || 0) +
        (stats.titleStats?.atk || 0) +
        (stats.tattooStats?.atk || 0) +
        (stats.ghostStats?.atk || 0);
      const flatCritical =
        (stats.itemStats?.critical || 0) +
        (stats.bonusStats?.critical || 0) +
        (stats.stats?.critical || 0) +
        (stats.traitStats?.critical || 0) +
        (stats.skillStats?.critical || 0) +
        (stats.titleStats?.critical || 0) +
        (stats.tattooStats?.critical || 0) +
        (stats.ghostStats?.critical || 0);
      const flatEvasion =
        (stats.itemStats?.evasion || 0) +
        (stats.bonusStats?.evasion || 0) +
        (stats.stats?.evasion || 0) +
        (stats.traitStats?.evasion || 0) +
        (stats.skillStats?.evasion || 0) +
        (stats.titleStats?.evasion || 0) +
        (stats.tattooStats?.evasion || 0) +
        (stats.ghostStats?.evasion || 0);
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: stamina * 20 + flatMaxHp,
        atk: str * 2 + flatAtk,
        critical: Math.floor((int / 2) + flatCritical),
        evasion: Math.floor((agi / 2) + flatEvasion),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
        flee: Math.floor(((agi / 2) + (int / 2)) / 2),
      };
    });
    const currentHealth = computed(() => {
      const damage = characterPrivateData.value?.damageTaken || 0;
      return Math.max(0, calculatedStats.value.maxHp - damage);
    });
    const infectionLevel = computed(() => characterPrivateData.value?.infectionLevel || 0);
    const fleeChance = computed(() => {
      if (!opponentData.value || !characterPrivateData.value) return 5;
      const playerFlee = calculatedStats.value.flee;
      const opponentFlee = opponentStats.value.flee;
      const fleeBase = Math.max(0, playerFlee - opponentFlee);
      const fleeValue = Math.floor(fleeBase / 2);
      return Math.min(100, 10 + fleeValue);
    });
    const selectedWeight = computed(() => {
      return rewards.value
        .filter((reward) => selectedRewardIds.value.includes(reward.id))
        .reduce((sum, reward) => sum + (reward.weight || 0), 0);
    });
    const rarityColors = computed(() => (rarity) => {
      switch (rarity) {
        case 1: return { bgColor: 'bg-gray-500', textColor: 'text-gray-500', borderColor: 'border-gray-600' };
        case 2: return { bgColor: 'bg-stone-500', textColor: 'text-stone-500', borderColor: 'border-stone-600' };
        case 3: return { bgColor: 'bg-green-500', textColor: 'text-green-500', borderColor: 'border-green-600' };
        case 4: return { bgColor: 'bg-blue-500', textColor: 'text-blue-500', borderColor: 'border-blue-600' };
        case 5: return { bgColor: 'bg-red-500', textColor: 'text-red-500', borderColor: 'border-red-600' };
        case 6: return { bgColor: 'bg-purple-500', textColor: 'text-purple-500', borderColor: 'border-purple-600' };
        case 7: return { bgColor: 'bg-orange-500', textColor: 'text-orange-500', borderColor: 'border-orange-600' };
        case 8: return { bgColor: 'bg-pink-500', textColor: 'text-pink-500', borderColor: 'border-pink-600' };
        case 9: return { bgColor: 'bg-yellow-500', textColor: 'text-yellow-500', borderColor: 'border-yellow-600' };
        case 10: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 11: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 12: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 13: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 14: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 15: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 16: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 17: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 18: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 19: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        case 20: return { bgColor: 'bg-amber-500', textColor: 'text-amber-500', borderColor: 'border-amber-600' };
        default: return { bgColor: 'bg-gray-700', textColor: 'text-gray-500', borderColor: 'border-gray-600' };
      }
    });
    const randomPhrase = computed(() => {
      if (selectedMerchant.value && selectedMerchant.value.phrases && selectedMerchant.value.phrases.length > 0) {
        const phrases = selectedMerchant.value.phrases;
        return phrases[Math.floor(Math.random() * phrases.length)];
      }
      return 'เลือกไอเท็มด้านล่าง หรือกดซื้อทันที';
    });
    const mutantPhrase = computed(() => {
      if (selectedMutant.value && selectedMutant.value.phrases && selectedMutant.value.phrases.length > 0) {
        const phrases = selectedMutant.value.phrases;
        return phrases[Math.floor(Math.random() * phrases.length)];
      }
      return 'อยากลองดีก็มาเลย';
    });
    const villagerPhrase = computed(() => {
      if (selectedVillager.value && selectedVillager.value.phrases && selectedVillager.value.phrases.length > 0) {
        const phrases = selectedVillager.value.phrases;
        return phrases[Math.floor(Math.random() * phrases.length)];
      }
      return 'จ้างฉันสิ ฉันจะช่วยคุณ';
    });
    const capacity = computed(() => Math.max(0, (maxLoad.value || 0) - (currentWeight.value || 0)));
    const occupierDisplayName = computed(() => {
      if (isNPC.value) return 'NPC ผู้ยึดครอง';
      if (isChallenging.value) {
        return occupierData.value?.characterName || 'ไม่ทราบชื่อ';
      }
      return occupierData.value?.characterName || 'ผู้ยึดครอง';
    });
    const merchantStats = computed(() => {
      if (!selectedMerchant.value)
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0 };
      const stats = selectedMerchant.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor(((stats.int || 0) / 2) + (stats.critical || 0)),
        evasion: Math.floor(((stats.agi || 0) / 2) + (stats.evasion || 0)),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });
    const mutantStats = computed(() => {
      if (!selectedMutant.value)
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0 };
      const stats = selectedMutant.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor(((stats.int || 0) / 2) + (stats.critical || 0)),
        evasion: Math.floor(((stats.agi || 0) / 2) + (stats.evasion || 0)),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });
    const villagerStats = computed(() => {
      if (!selectedVillager.value)
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0 };
      const stats = selectedVillager.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor(((stats.int || 0) / 2) + (stats.critical || 0)),
        evasion: Math.floor(((stats.agi || 0) / 2) + (stats.evasion || 0)),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });
    const availablePerks = [
      { name: 'ผู้รอดชีวิต', description: 'ถ้าถูกฆ่าตายมีโอกาสกลับไปที่แคมป์ (โอกาส 50%)' },
      { name: 'บ้าคลั่ง', description: 'เมื่อมีเลือดต่ำกว่า 50 % มีโอกาเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักวางกับดัก', description: 'เมื่ออยู่ฝ่ายป้องกันมีโอกาสเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักบุกโจมตี', description: 'เมื่ออยู่ฝ่ายโจมตีมีโอกาสเพิ่มลูกเต๋า 1 โอกาส (โอกาส 20%)' },
      { name: 'อึดถึกทน', description: 'ลดดาเมจที่ได้รับ 20% (โอกาส 30%)' },
      { name: 'ฟื้นตัวไว', description: 'รักษาเลือด 10% max HP หลังรอบ (โอกาส 10%)' },
      { name: 'หลอกล่อ', description: 'มีโอกาสทำให้อีกฝ่ายลดลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'ผู้มีบุญ', description: 'เมื่อ hp เหลือ 0 มีโอกาสที่จะไม่ตายและกลับมามีเลือด 20% (โอกาส 10%)' },
      { name: 'หัวหน้าทีม', description: 'Heal ทีม 10 % max HP หลังรอบ (โอกาส 10%)' },
      { name: 'ฆาตกรโรคจิต', description: 'มีโอกาส +2 dice ถ้าทีมเหลือ 2 คน (โอกาส 20%)' },
      { name: 'นักเจรจา', description: 'เมื่อทอยลูกเต๋าแพ้มีโอกาสที่จะไม่โดนโจมตีเลย (โอกาส 10%)' },
      { name: 'ผู้ใช้อาวุธระยะไกล', description: '+10% crit ถ้าแต้มลูกเต๋านำมากกว่า 3 แต้ม (โอกาส 100%)' }
    ];
    const getPerkDescription = (p) => availablePerks.find(x => x.name === p)?.description || '';
    // ===== Helpers =====
    const formatTime = (ms) => {
      if (ms <= 0) return 'หมดเวลา!';
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / (1000 * 60)) % 60);
      return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
    };
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };
    const startCountdown = () => {
      if (expiresAt.value && !timer) {
        timeLeft.value = expiresAt.value - Date.now();
        if (timeLeft.value > 0) {
          timer = setInterval(() => {
            const now = Date.now();
            timeLeft.value = expiresAt.value - now;
            if (timeLeft.value <= 0) {
              clearInterval(timer);
              timer = null;
              if (!fightResult.value) {
                fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
                stopAutoAttack('อีเวนต์หมดเวลา');
              }
            }
          }, 1000);
        } else {
          timeLeft.value = 0;
          if (!fightResult.value) {
            fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
            stopAutoAttack('อีเวนต์หมดเวลา');
          }
        }
      }
    };
    const stopCountdown = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    // ===== Close Handler =====
    const closeModal = () => {
      isClosed.value = true;
      fightResult.value = 'หน้าต่างถูกปิด';
      stopAutoAttack('หน้าต่างถูกปิด');
      emit('close');
    };
    const getEntityMaxHp = (entity) => {
      if (!entity) return 0;
      const stats = entity.stats || {};
      return (stats.stamina || 0) * 20 + (stats.maxHp || 0);
    };
    const toEmbeddedEntity = (id, data) => {
      if (!id || !data) return null;
      return { id, ...data };
    };
    const getItemTemplateByName = (itemName) => {
      if (!itemName) return null;
      return gameItemsData[itemName] || null;
    };
    const toRewardPreview = (id, rewardData) => {
      if (!rewardData) return null;
      const itemTemplate = getItemTemplateByName(rewardData.itemName || rewardData.name) || {};
      const rarity = rewardData.rarity || itemTemplate.rarity || 1;
      return {
        id,
        ...rewardData,
        name: rewardData.name || rewardData.itemName || 'ไม่ทราบชื่อ',
        img: rewardData.img || rewardData.imageUrl || itemTemplate.img || itemTemplate.imageUrl || 'default-item.png',
        weight: rewardData.weight || 0,
        transfer: rewardData.transfer ?? rewardData.transferable ?? false,
        rarityColors: rewardData.rarityColors || itemTemplate.rarityColors || rarityColors.value(rarity),
      };
    };
    const toMerchantPreview = (id, merchantData) => {
      if (!merchantData) return null;
      return {
        id,
        ...merchantData,
        items: (merchantData.items || []).map((item) => {
          const itemTemplate = getItemTemplateByName(item.name || item.itemName) || {};
          const itemRarity = item.rarity || itemTemplate.rarity || 1;
          return {
            ...item,
            img: item.img || item.imageUrl || itemTemplate.img || itemTemplate.imageUrl || 'default-item.png',
            rarity: itemRarity,
            rarityColors: item.rarityColors || itemTemplate.rarityColors || rarityColors.value(itemRarity),
          };
        }),
        robRewards: merchantData.robRewards || { diamond: 0, gold: 0, silver: 0 },
        allowRob: merchantData.allowRob || false,
        angryFace: merchantData.angryFace || null,
        phrases: merchantData.phrases || [],
      };
    };
    const toMutantPreview = (id, mutantData) => {
      if (!mutantData) return null;
      return {
        id,
        ...mutantData,
        phrases: mutantData.phrases || [],
        canCatch: mutantData.canCatch || false,
        catchDifficulty: mutantData.catchDifficulty || 1,
        catchItems: mutantData.catchItems || [],
        catchLimit: mutantData.catchLimit || 1,
        damageTaken: mutantData.damageTaken || 0,
        catchAttempts: mutantData.catchAttempts || 0,
      };
    };
    const toVillagerPreview = (id, villagerData) => {
      if (!villagerData) return null;
      return {
        id,
        ...villagerData,
        hireCosts: villagerData.hireCosts || { diamond: 0, gold: 0, silver: 0 },
        normalFace: villagerData.normalFace || null,
        attackDice: villagerData.attackDice || 0,
        defenseDice: villagerData.defenseDice || 0,
        phrases: villagerData.phrases || [],
        weapon: villagerData.weapon || '',
        strongAgainst: villagerData.strongAgainst || [],
        weakAgainst: villagerData.weakAgainst || [],
        perks: villagerData.perks || [],
      };
    };
    // ===== Data Fetchers =====
    const fetchRewards = async (rewardItemIds, rewardDataMap = eventData.value.rewardDataMap) => {
      rewards.value = [];
      if (eventData.value.diamondReward > 0) {
        rewards.value.push({
          id: 'diamond',
          name: 'เพชร',
          img: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%9E%E0%B8%8A%E0%B8%A3.png?alt=media&token=d0f95851-b518-46cd-aa78-4729cc6d9f41',
          amount: eventData.value.diamondReward,
          weight: 0,
          rarity: 10,
          rarityColors: rarityColors.value(10),
          transfer: false,
        });
      }
      for (const itemId of rewardItemIds || []) {
        const embeddedReward = toRewardPreview(itemId, rewardDataMap?.[itemId]);
        if (embeddedReward) {
          rewards.value.push(embeddedReward);
        }
      }
      selectedRewardIds.value = [];
    };
    const fetchSituationRewards = async (situationsData) => {
      return (situationsData || []).map((situation) => ({
        ...situation,
        rewards: (situation.rewards || []).map((reward) => {
          const itemTemplate = getItemTemplateByName(reward.itemName || reward.name) || {};
          const rarity = reward.rarity || itemTemplate.rarity || 1;
          return {
            ...reward,
            name: reward.name || reward.itemName,
            img: reward.img || reward.imageUrl || itemTemplate.img || itemTemplate.imageUrl || 'default-item.png',
            rarityColors: reward.rarityColors || itemTemplate.rarityColors || rarityColors.value(rarity),
          };
        }),
      }));
    };
    const subscribeMerchants = async (merchantIds) => {
      unsubscribeMerchants.value.forEach((unsub) => unsub());
      unsubscribeMerchants.value = [];
      merchants.value = (merchantIds || [])
        .map((id) => toMerchantPreview(id, eventData.value.merchantDataMap?.[id]))
        .filter(Boolean);
      if (!selectedMerchantId.value || !merchants.value.some((merchant) => merchant.id === selectedMerchantId.value)) {
        selectedMerchantId.value = merchants.value[0]?.id || null;
      }
    };
    const subscribeMutants = async (mutantIds) => {
      unsubscribeMutants.value.forEach((unsub) => unsub());
      unsubscribeMutants.value = [];
      mutants.value = (mutantIds || [])
        .map((id) => toMutantPreview(id, eventData.value.mutantDataMap?.[id]))
        .filter(Boolean);
      if (!selectedMutantId.value || !mutants.value.some((mutant) => mutant.id === selectedMutantId.value)) {
        selectedMutantId.value = mutants.value[0]?.id || null;
      }
    };
    const subscribeVillagers = async (villagerIds) => {
      unsubscribeVillagers.value.forEach((unsub) => unsub());
      unsubscribeVillagers.value = [];
      villagers.value = (villagerIds || [])
        .map((id) => toVillagerPreview(id, eventData.value.villagerDataMap?.[id]))
        .filter(Boolean);
      if (!selectedVillagerId.value || !villagers.value.some((villager) => villager.id === selectedVillagerId.value)) {
        selectedVillagerId.value = villagers.value[0]?.id || null;
      }
    };
    const fetchOccupierData = async (mapName) => {
      if (!mapName) return;
      const mapDoc = await projectFirestore.collection('maps').doc(mapName).get();
      if (mapDoc.exists) {
        const occupierUid = mapDoc.data().occupierUid;
        if (occupierUid) {
          isNPC.value = false;
          const charDoc = await projectFirestore.collection('characters').doc(occupierUid).get();
          if (charDoc.exists) {
            const charData = charDoc.data();
            occupierData.value = {
              characterName: charData.characterName || 'N/A',
              imageUrl: charData.imageUrl || 'default-character.png'
            };
          }
        } else {
          isNPC.value = true;
          occupierData.value = {
            characterName: 'NPC ผู้ยึดครอง',
            imageUrl: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/merchant%2F%E0%B8%9E%E0%B8%A3%E0%B8%B0%E0%B8%9A%E0%B8%A3%E0%B8%A1%E0%B8%A1%E0%B8%AB%E0%B8%B2%E0%B8%A3%E0%B8%B2%E0%B8%8A%E0%B8%A7%E0%B8%B1%E0%B8%87%2F%E0%B8%A3%E0%B8%B4%E0%B8%95%E0%B8%A0%E0%B8%9E.png?alt=media&token=c2f45d24-2cd6-485d-bfc6-bc6ade04e0c4'
          };
        }
      }
    };
    // ===== UI Actions =====
    const toggleRewardSelection = (rewardId) => {
      const reward = rewards.value.find((r) => r.id === rewardId);
      if (!reward) {
        fightResult.value = 'ไม่พบรางวัลที่เลือก';
        return;
      }
      if (!reward) {
        fightResult.value = 'เพชรจะได้รับตอนกลับบ้านอัตโนมัติ';
        fightResult.value = 'เพชรจะได้รับตอนกลับบ้านอัตโนมัติ';
        return;
      }
      const newWeight = selectedRewardIds.value.includes(rewardId)
        ? selectedWeight.value
        : selectedWeight.value + (reward.weight || 0);
      if (newWeight > maxLoad.value - currentWeight.value) {
        fightResult.value = 'น้ำหนักเกิน! ไม่สามารถเลือกไอเทมเพิ่มได้';
        return;
      }
      if (selectedRewardIds.value.includes(rewardId)) {
        selectedRewardIds.value = selectedRewardIds.value.filter((id) => id !== rewardId);
      } else {
        selectedRewardIds.value = [...selectedRewardIds.value, rewardId];
      }
      fightResult.value = '';
    };
    const selectAllRewards = () => {
      const realRewards = rewards.value;
      if (!realRewards.length) {
        fightResult.value = 'ไม่มีไอเทมให้เลือก!';
        return;
      }
      const availableWeight = maxLoad.value - currentWeight.value;
      let totalWeight = 0;
      const selectableRewards = realRewards.filter((reward) => {
        totalWeight += reward.weight || 0;
        return totalWeight <= availableWeight;
      });
      if (selectableRewards.length === 0) {
        fightResult.value = 'น้ำหนักเกิน! ไม่สามารถเลือกไอเทมทั้งหมดได้';
        return;
      }
      selectedRewardIds.value = selectableRewards.map((reward) => reward.id);
      fightResult.value = `เลือก ${selectableRewards.length} ไอเทมเรียบร้อย`;
    };
    const selectSituation = (situationName) => {
      selectedSituationName.value = situationName;
      fightResult.value = `เลือกสถานการณ์ "${situationName}"`;
    };
    const toggleAllSelections = () => {
      if (selectedRewardIds.value.length) {
        selectedRewardIds.value = [];
        fightResult.value = 'ยกเลิกการเลือกทั้งหมดแล้ว';
        return;
      }
      if (selectedRewardIds.value.length) {
        fightResult.value = latestMessage || 'นำรางวัลใส่กระเป๋าเรียบร้อย';
        fightResult.value = latestMessage || 'นำรางวัลใส่กระเป๋าเรียบร้อย';
        selectedRewardIds.value = [];
        lockGoHomeTemporarily();
        if (eventFinished) {
          stopCountdown();
          stopAutoAttack();
          showFleeOptions.value = false;
        }
        if (eventFinished) {
          stopCountdown();
          stopAutoAttack();
          showFleeOptions.value = false;
        }
        fightResult.value = 'ยกเลิกการเลือกทั้งหมดแล้ว';
      } else {
        selectAllRewards();
      }
    };
    // ===== Auto Attack Functions =====
    const toggleAutoAttack = (intervalMs) => {
      if (isAutoAttacking.value) {
        stopAutoAttack('ผู้เล่นปิด Auto Attack');
      } else {
        startAutoAttack(intervalMs);
        fightResult.value = `Auto Attack ถูกเปิด (${intervalMs}ms)`;
      }
    };
    const startAutoAttack = (intervalMs) => {
      if (isAutoAttacking.value) return;
      isAutoAttacking.value = true;
      autoAttackIntervalMs.value = intervalMs;
      noZombieSince = null;
      if (autoAttackInterval) clearInterval(autoAttackInterval);
      autoAttackInterval = setInterval(async () => {
        if (opponentType.value === 'zombie' && (energyLevel.value || 0) <= 0) {
          stopAutoAttack('พลังงานหมด: Auto Attack หยุด');
          return;
        }
        if (!opponentData.value) {
          if (noZombieSince == null) noZombieSince = Date.now();
          const waiting = Date.now() - noZombieSince;
          if (waiting >= NO_ZOMBIE_GRACE_MS) {
            stopAutoAttack('ไม่มีศัตรูต่อ: Auto Attack หยุด');
          }
          return;
        } else {
          noZombieSince = null;
        }
        if (!isFighting.value) {
          try {
            await attackOpponentHandler();
          } catch (e) {
            const msg = (e?.message || '').toString();
            const code = e?.code || '';
            const energyErr =
              msg.includes('พลังงานไม่เพียงพอ') ||
              (code === 'failed-precondition' && (energyLevel.value || 0) <= 0);
            if (energyErr && opponentType.value === 'zombie') {
              stopAutoAttack('พลังงานหมด: Auto Attack หยุด');
            }
          }
        }
      }, intervalMs);
    };
    const stopAutoAttack = (reason = '') => {
      if (autoAttackInterval) {
        clearInterval(autoAttackInterval);
        autoAttackInterval = null;
      }
      isAutoAttacking.value = false;
      autoAttackIntervalMs.value = null;
      if (reason) fightResult.value = reason;
    };
    // ===== Cloud Function Actions =====
    const attackOpponentHandler = async () => {
      if (isFighting.value || (opponentType.value === 'zombie' && energyLevel.value <= 0)) return;
      isFighting.value = true;
      fightResult.value = 'กำลังโจมตี...';
      try {
        let callable;
        if (opponentType.value === 'zombie') {
          const response = await gameActions({ action: 'zombieActions', subAction: 'attack', eventId: props.eventId });
          fightResult.value = response.data.message;
        } else if (opponentType.value === 'merchant') {
          const response = await gameActions({ action: 'merchantActions', subAction: 'attackMerchant', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data?.message?.includes('พ่อค้าตาย')) {
            activeTab.value = 'rewards';
          }
        } else if (opponentType.value === 'mutant') {
          const response = await gameActions({ action: 'mutantActions', subAction: 'attackMutant', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data?.message?.includes('สัตว์กลายพันธุ์ตาย')) {
            activeTab.value = 'rewards';
          }
        } else if (opponentType.value === 'occupier') {
          const response = await gameActions({ action: 'occupierActions', subAction: 'attackOccupier', eventId: props.eventId });
          fightResult.value = response.data.message;
        }
        if (fightResult.value.includes('ผู้เล่นตาย') || fightResult.value.includes('การท้าประลองสิ้นสุดลง') || fightResult.value.includes('ผู้ยึดครองถูกเคลียร์')) {
          stopCountdown();
          stopAutoAttack('ผู้เล่นตายหรือการท้าประลองสิ้นสุด');
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการโจมตี';
      } finally {
        isFighting.value = false;
      }
    };
    const fleeOpponentHandler = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังหนี...';
      try {
        let callable;
        if (opponentType.value === 'zombie') {
          const response = await gameActions({ action: 'zombieActions', subAction: 'flee', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data.completed) {
            stopCountdown();
            stopAutoAttack('หลบหนีสำเร็จ');
            showFleeOptions.value = false;
          }
        } else if (opponentType.value === 'merchant') {
          const response = await gameActions({ action: 'merchantActions', subAction: 'fleeMerchant', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data.completed) {
            stopCountdown();
            stopAutoAttack('หลบหนีสำเร็จ');
            showFleeOptions.value = false;
          }
        } else if (opponentType.value === 'mutant') {
          const response = await gameActions({ action: 'mutantActions', subAction: 'fleeMutant', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data.completed) {
            stopCountdown();
            stopAutoAttack('หลบหนีสำเร็จ');
            showFleeOptions.value = false;
          }
        }  else if (opponentType.value === 'occupier') {
          const response = await gameActions({ action: 'occupierActions', subAction: 'fleeOccupier', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data.completed) {
            stopCountdown();
            stopAutoAttack('หลบหนีสำเร็จ');
            showFleeOptions.value = false;
          }
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการหนี';
      } finally {
        isFighting.value = false;
      }
    };
    const fleeBySilver = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังหนีด้วยเหรียญเงิน...';
      try {
        const response = await gameActions({ action: 'zombieActions', subAction: 'fleeBySilver', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data.completed) {
          stopCountdown();
          stopAutoAttack('หลบหนีสำเร็จ');
          showFleeOptions.value = false;
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการหนีด้วยเหรียญเงิน';
      } finally {
        isFighting.value = false;
      }
    };
    const handleBuyClick = (merchantId, itemName) => {
      if (isFighting.value) return;
      if (isConfirmingBuy.value && selectedItemToBuy.value === itemName) {
        buyItem(merchantId, itemName);
        isConfirmingBuy.value = false;
        selectedItemToBuy.value = null;
        if (confirmBuyTimeout) {
          clearTimeout(confirmBuyTimeout);
          confirmBuyTimeout = null;
        }
      } else {
        isConfirmingBuy.value = true;
        selectedItemToBuy.value = itemName;
        fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการซื้อ';
        confirmBuyTimeout = setTimeout(() => {
          isConfirmingBuy.value = false;
          selectedItemToBuy.value = null;
          fightResult.value = '';
        }, 5000);
      }
    };
    const buyItem = async (merchantId, itemName) => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังซื้อ...';
      try {
        const response = await gameActions({
          action: 'merchantActions',
          subAction: 'buyFromMerchant',
          eventId: props.eventId,
          merchantId,
          itemName
        });
        fightResult.value = response.data.message;
        selectedItemName.value = null;
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการซื้อ';
      } finally {
        isFighting.value = false;
      }
    };
    const robMerchant = async (merchantId) => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังปล้น...';
      try {
        const response = await gameActions({ action: 'merchantActions', subAction: 'robMerchant', eventId: props.eventId, merchantId });
        fightResult.value = response.data.message;
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการปล้น';
      } finally {
        isFighting.value = false;
      }
    };
    const fightMutant = async (mutantId) => {
  if (isFighting.value) return;

  if (isAutoAttacking.value) {
    stopAutoAttack();
  }

  isFighting.value = true;
  fightResult.value = 'กำลังเริ่มต่อสู้...';

  try {
    const response = await gameActions({
      action: 'mutantActions',
      subAction: 'fightMutant',
      eventId: props.eventId,
      mutantId
    });
    fightResult.value = response.data.message;
  } catch (err) {
    fightResult.value = err.message || 'เกิดข้อผิดพลาดในการเริ่มต่อสู้';
  } finally {
    isFighting.value = false;
  }
};
    const captureMutant = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังจับ...';
      try {
        const response = await gameActions({ action: 'mutantActions', subAction: 'captureMutant', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data?.isCaptureSuccessful && Number(response.data?.rewardCount || 0) > 0) {
          activeTab.value = 'rewards';
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการจับ';
      } finally {
        isFighting.value = false;
      }
    };
    const handleFightClick = (mutantId) => {
      if (isFighting.value) return;
      if (isConfirmingFight.value) {
        fightMutant(mutantId);
        isConfirmingFight.value = false;
        if (confirmFightTimeout) {
          clearTimeout(confirmFightTimeout);
          confirmFightTimeout = null;
        }
      } else {
        isConfirmingFight.value = true;
        fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการต่อสู้';
        confirmFightTimeout = setTimeout(() => {
          isConfirmingFight.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    const handleCaptureClick = () => {
      if (isFighting.value) return;
      if (isConfirmingCapture.value) {
        captureMutant();
        isConfirmingCapture.value = false;
        if (confirmCaptureTimeout) {
          clearTimeout(confirmCaptureTimeout);
          confirmCaptureTimeout = null;
        }
      } else {
        isConfirmingCapture.value = true;
        fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการจับ';
        confirmCaptureTimeout = setTimeout(() => {
          isConfirmingCapture.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    const handleHireClick = (villagerId) => {
      if (isFighting.value) return;
      if (isConfirmingHire.value) {
        hireVillager(villagerId);
        isConfirmingHire.value = false;
        if (confirmHireTimeout) {
          clearTimeout(confirmHireTimeout);
          confirmHireTimeout = null;
        }
      } else {
        isConfirmingHire.value = true;
        fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการจ้าง';
        confirmHireTimeout = setTimeout(() => {
          isConfirmingHire.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    const hireVillager = async (villagerId) => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังจ้างชาวบ้าน...';
      try {
        const response = await gameActions({ action: 'villagerActions', subAction: 'hireVillager', eventId: props.eventId, villagerId });
        fightResult.value = response.data.message;
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการจ้างชาวบ้าน';
      } finally {
        isFighting.value = false;
      }
    };
    const handleGoHomeClick = () => {
      if (isFighting.value) return;
      if (isConfirmingGoHome.value) {
        goHome();
        isConfirmingGoHome.value = false;
        if (confirmTimeout) {
          clearTimeout(confirmTimeout);
          confirmTimeout = null;
        }
      } else {
        isConfirmingGoHome.value = true;
        fightResult.value = 'คลิกปุ่มอีกครั้งเพื่อยืนยันการกลับบ้าน';
        confirmTimeout = setTimeout(() => {
          isConfirmingGoHome.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    const goHome = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังกลับบ้าน...';
      try {
        const response = await gameActions({ action: 'goHome', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data?.safe) {
          stopCountdown();
          showFleeOptions.value = false;
          selectedRewardIds.value = [];
        stopAutoAttack('กลับบ้านแล้ว');
        } else {
          stopAutoAttack();
        }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการกลับบ้าน';
      } finally {
        isFighting.value = false;
      }
    };
const addToBag = async () => {
  if (isAddingToBag.value || !selectedRewardIds.value.length || !hasReward.value) return;

  isAddingToBag.value = true;
  fightResult.value = 'กำลังนำใส่กระเป๋า.';

  try {
    const selectedIds = [...selectedRewardIds.value];

    if (!selectedIds.length) {
      fightResult.value = 'กรุณาเลือกรางวัลก่อน';
      return;
    }

    const payload = {
      eventId: props.eventId,
      rewardItemIds: selectedIds
    };

    const response = await gameActions({
      action: 'addToBag100',
      ...payload
    });

    selectedRewardIds.value = [];
    fightResult.value = response.data?.message || 'นำรางวัลใส่กระเป๋าเรียบร้อย';

    lockGoHomeTemporarily();

    if (response.data?.eventFinished) {
      stopCountdown();
      stopAutoAttack();
      showFleeOptions.value = false;
    }

    return;
  } catch (err) {
    fightResult.value = err.message || 'เกิดข้อผิดพลาดในการนำใส่กระเป๋า';
  } finally {
    isAddingToBag.value = false;
  }
};    const continueExploring = async () => {
      if (isFighting.value || !hasSituation.value || !selectedSituationName.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังสำรวจสถานการณ์ต่อ...';
      try {
        const response = await gameActions({
          action: 'continueExploring',
          eventId: props.eventId,
          situationName: selectedSituationName.value,
        });
        fightResult.value = response.data.message;
        hasSituation.value = false;
        situations.value = [];
        selectedSituationName.value = null;
        activeTab.value = 'rewards';
        lockGoHomeTemporarily();
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการสำรวจต่อ';
      } finally {
        isFighting.value = false;
      }
    };
    const handleChallengeClick = () => {
      if (isFighting.value) return;
      if (isConfirmingChallenge.value) {
        challengeOccupier();
        isConfirmingChallenge.value = false;
        if (confirmChallengeTimeout) {
          clearTimeout(confirmChallengeTimeout);
          confirmChallengeTimeout = null;
        }
      } else {
        isConfirmingChallenge.value = true;
        fightResult.value = 'คลิกปุ่มอีกครั้งเพื่อยืนยันการท้าประลอง';
        confirmChallengeTimeout = setTimeout(() => {
          isConfirmingChallenge.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    const challengeOccupier = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังท้าประลอง...';
      try {
        const response = await gameActions({ action: 'occupierActions', subAction: 'challengeOccupier', eventId: props.eventId });
        fightResult.value = response.data.message;
        isChallenging.value = true;
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการท้าประลอง';
      } finally {
        isFighting.value = false;
      }
    };
    const handleClearClick = () => {
      if (isFighting.value) return;
      if (isConfirmingClear.value) {
        clearOccupier();
        isConfirmingClear.value = false;
        if (confirmClearTimeout) {
          clearTimeout(confirmClearTimeout);
          confirmClearTimeout = null;
        }
      } else {
        isConfirmingClear.value = true;
        fightResult.value = 'คลิกปุ่มอีกครั้งเพื่อยืนยันการเคลียร์ผู้ยึดครอง';
        confirmClearTimeout = setTimeout(() => {
          isConfirmingClear.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    const clearOccupier = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังเคลียร์ผู้ยึดครอง...';
      try {
        const response = await gameActions({ action: 'occupierActions', subAction: 'clearOccupier', eventId: props.eventId });
        fightResult.value = response.data.message;
        isChallenging.value = true;
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการเคลียร์ผู้ยึดครอง';
      } finally {
        isFighting.value = false;
      }
    };
    const handleRobClick = (merchantId) => {
      if (isFighting.value) return;
      if (isConfirmingRob.value) {
        robMerchant(merchantId);
        isConfirmingRob.value = false;
        if (confirmRobTimeout) {
          clearTimeout(confirmRobTimeout);
          confirmRobTimeout = null;
        }
      } else {
        isConfirmingRob.value = true;
        fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการปล้น';
        confirmRobTimeout = setTimeout(() => {
          isConfirmingRob.value = false;
          fightResult.value = '';
        }, 5000);
      }
    };
    // ===== Watchers =====
    watch(() => inventoryStore.bagData, (bagData) => {
      if (bagData) {
        currentWeight.value = bagData.currentWeight || 0;
        maxLoad.value = bagData.maxLoad || 100;
      }
    }, { immediate: true });
    watch(selectedMerchant, (newMerchant) => {
      if (newMerchant && newMerchant.items.length > 0) {
        selectedItemName.value = newMerchant.items[0].name;
      } else {
        selectedItemName.value = null;
      }
    });
    watch(hasReward, (newVal) => {
      if (newVal) {
        activeTab.value = 'rewards';
      }
    });
    watch(hasReward, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    activeTab.value = 'rewards';
    lockGoHomeTemporarily(2000);
  }
  if (newVal && isAutoAttacking.value) {
    stopAutoAttack('ซอมบี้หมด: Auto Attack หยุด');
  }
});
    watch(() => props.eventId, (newEventId, oldEventId) => {
      if (newEventId === oldEventId) return;
      stopCountdown();
      stopAutoAttack();
      if (goHomeUnlockTimeout) {
        clearTimeout(goHomeUnlockTimeout);
        goHomeUnlockTimeout = null;
      }
      subscribeToData();
    });
    // ===== Firestore Subscription =====
    const subscribeToData = async () => {
      try {
        resetEmbeddedEventSubscriptions();
        if (!props.eventId) {
          resetEmbeddedEventState();
          stopCountdown();
          isLoading.value = false;
          return;
        }
        isLoading.value = true;
        unsubscribeEvent = projectFirestore.collection('events').doc(props.eventId)
          .onSnapshot(async (doc) => {
            if (doc.exists) {
              const data = doc.data();
              eventData.value = {
                ...data,
                escapeCoins: data.escapeCoins || 0,
                escapeAttempts: data.escapeAttempts || 0,
                diamondReward: data.diamondReward || 0,
                challengeCostDiamonds: data.challengeCostDiamonds || 0,
                isClearing: data.isClearing || false,
              };
              hasReward.value = Boolean(data.hasReward || (data.rewardItemIds?.length > 0) || (data.diamondReward || 0) > 0);
              hasSituation.value = data.hasSituation || false;
              hasMerchant.value = Boolean(data.hasMerchant || data.merchantIds?.length > 0 || data.currentMerchantId);
              hasMutant.value = Boolean(data.hasMutant || data.mutantIds?.length > 0 || data.currentMutantId);
              hasVillager.value = Boolean(data.hasVillager || data.villagerIds?.length > 0 || data.currentVillagerId);
              encounterOccupier.value = data.encounterOccupier || false;
              isChallenging.value = data.isChallenging || false;
              hasOccupier.value = encounterOccupier.value && !isChallenging.value;
              zombieKilled.value = data.zombieKilled || 0;
              mapName.value = data.mapName || '';
              currentMerchantId.value = data.currentMerchantId || null;
              if (data.situations?.length) {
                situations.value = await fetchSituationRewards(data.situations);
              } else {
                situations.value = [];
              }
              if (encounterOccupier.value && !occupierData.value) {
                await fetchOccupierData(mapName.value);
              }
              const newExpiresAt = data.expiresAt?.toDate().getTime() || null;
              if (newExpiresAt !== expiresAt.value) {
                expiresAt.value = newExpiresAt;
                stopCountdown();
                if (expiresAt.value) startCountdown();
              } else if (expiresAt.value && !timer) {
                startCountdown();
              }
              // -- Zombie --
              const embeddedZombie = toEmbeddedEntity(data.currentZombieId, data.zombieDataMap?.[data.currentZombieId]);
              if (embeddedZombie) {
                if (unsubscribeZombie) {
                  unsubscribeZombie();
                  unsubscribeZombie = null;
                }
                zombieData.value = embeddedZombie;
                currentOpponentHp.value = Math.max(0, getEntityMaxHp(embeddedZombie) - (embeddedZombie.damageTaken || 0));
              } else {
                if (unsubscribeZombie) {
                  unsubscribeZombie();
                  unsubscribeZombie = null;
                }
                zombieData.value = null;
                currentOpponentHp.value = 0;
              }
              // -- Merchant --
              const embeddedMerchant = toEmbeddedEntity(data.currentMerchantId, data.merchantDataMap?.[data.currentMerchantId]);
              if (embeddedMerchant) {
                if (unsubscribeMerchant) {
                  unsubscribeMerchant();
                  unsubscribeMerchant = null;
                }
                merchantData.value = embeddedMerchant;
                currentOpponentHp.value = getEntityMaxHp(embeddedMerchant);
              } else {
                if (unsubscribeMerchant) {
                  unsubscribeMerchant();
                  unsubscribeMerchant = null;
                }
                merchantData.value = null;
                if (!data.currentZombieId) currentOpponentHp.value = 0;
              }
              // -- Mutant --
              const embeddedMutant = toEmbeddedEntity(data.currentMutantId, data.mutantDataMap?.[data.currentMutantId]);
              if (embeddedMutant) {
                if (unsubscribeMutant) {
                  unsubscribeMutant();
                  unsubscribeMutant = null;
                }
                mutantData.value = embeddedMutant;
                currentOpponentHp.value = Math.max(0, getEntityMaxHp(embeddedMutant) - (embeddedMutant.damageTaken || 0));
              } else {
                if (unsubscribeMutant) {
                  unsubscribeMutant();
                  unsubscribeMutant = null;
                }
                mutantData.value = null;
                if (!data.currentZombieId && !data.currentMerchantId) currentOpponentHp.value = 0;
              }
              const embeddedVillager = toEmbeddedEntity(data.currentVillagerId, data.villagerDataMap?.[data.currentVillagerId]);
              if (embeddedVillager) {
                villagerData.value = embeddedVillager;
                currentOpponentHp.value = Math.max(0, getEntityMaxHp(embeddedVillager) - (embeddedVillager.damageTaken || 0));
              } else if (!data.currentVillagerId) {
                villagerData.value = null;
                if (!data.currentZombieId && !data.currentMerchantId && !data.currentMutantId) currentOpponentHp.value = 0;
              }
              // When no opponent exists
              if (!data.currentZombieId && !data.currentMerchantId && !data.currentMutantId && !data.currentVillagerId) {
                showFleeOptions.value = false;
                if (hasReward.value) {
                  await fetchRewards(data.rewardItemIds, data.rewardDataMap);
                } else {
                  rewards.value = [];
                }
                if (hasMerchant.value) {
                  await subscribeMerchants(data.merchantIds);
                } else {
                  merchants.value = [];
                }
                if (hasMutant.value) {
                  await subscribeMutants(data.mutantIds);
                } else {
                  mutants.value = [];
                }
                if (hasVillager.value) {
                  await subscribeVillagers(data.villagerIds);
                } else {
                  villagers.value = [];
                }
                const validTabs = [];
                if (hasReward.value) validTabs.push('rewards');
                if (hasSituation.value) validTabs.push('situations');
                if (hasMerchant.value) validTabs.push('merchants');
                if (hasMutant.value) validTabs.push('mutants');
                if (hasVillager.value) validTabs.push('villagers');
                if (hasOccupier.value) validTabs.push('occupiers');
                if (!validTabs.includes(activeTab.value)) {
                  if (hasReward.value) {
                    activeTab.value = 'rewards';
                  } else if (hasSituation.value) {
                    activeTab.value = 'situations';
                  } else if (hasMerchant.value) {
                    activeTab.value = 'merchants';
                  } else if (hasMutant.value) {
                    activeTab.value = 'mutants';
                  } else if (hasVillager.value) {
                    activeTab.value = 'villagers';
                  } else if (hasOccupier.value) {
                    activeTab.value = 'occupiers';
                  }
                }
              }
            } else {
              resetEmbeddedEventState();
              stopCountdown();
            }
            isLoading.value = false;
          });

      } catch (_err) {
        fightResult.value = 'ไม่สามารถโหลดข้อมูลได้';
        isLoading.value = false;
      }
    };
    // ===== Lifecycle =====
    onMounted(() => {
      subscribeToData();
    });
    onUnmounted(() => {
      stopCountdown();
      stopAutoAttack();
      if (confirmTimeout) clearTimeout(confirmTimeout);
      if (confirmRobTimeout) clearTimeout(confirmRobTimeout);
      if (confirmFightTimeout) clearTimeout(confirmFightTimeout);
      if (confirmCaptureTimeout) clearTimeout(confirmCaptureTimeout);
      if (confirmChallengeTimeout) clearTimeout(confirmChallengeTimeout);
      if (confirmClearTimeout) clearTimeout(confirmClearTimeout);
      if (confirmBuyTimeout) clearTimeout(confirmBuyTimeout);
      if (confirmHireTimeout) clearTimeout(confirmHireTimeout);
      if (goHomeUnlockTimeout) clearTimeout(goHomeUnlockTimeout);
      if (unsubscribeEvent) unsubscribeEvent();
      if (unsubscribeZombie) unsubscribeZombie();
      if (unsubscribeMerchant) unsubscribeMerchant();
      if (unsubscribeMutant) unsubscribeMutant();
      if (unsubscribeVillager) unsubscribeVillager();
      unsubscribeMerchants.value.forEach((unsub) => unsub());
      unsubscribeMutants.value.forEach((unsub) => unsub());
      unsubscribeVillagers.value.forEach((unsub) => unsub());
    });
    // ===== Expose to template =====
    return {
      isAddingToBag,
      isClosed,
      closeModal,
      occupierDisplayName,
      capacity,
      toggleAllSelections,
      mapName,
      isLoading,
      zombieKilled,
      opponentData,
      opponentType,
      opponentStats,
      currentOpponentHp,
      characterData,
      characterPrivateData,
      calculatedStats,
      currentHealth,
      infectionLevel,
      energyLevel,
      maxEnergy,
      fightResult,
      isFighting,
      hasReward,
      hasSituation,
      hasMerchant,
      hasMutant,
      hasVillager,
      hasOccupier,
      situations,
      selectedSituationName,
      rewards,
      selectedRewardIds,
      merchants,
      mutants,
      villagers,
      selectedMerchantId,
      selectedMutantId,
      selectedVillagerId,
      selectedItemName,
      timeLeft,
      fleeChance,
      formatTime,
      handleImageError,
      attackOpponentHandler,
      toggleAutoAttack,
      fleeOpponentHandler,
      fleeBySilver,
      handleGoHomeClick,
      goHome,
      toggleRewardSelection,
      selectAllRewards,
      addToBag,
      continueExploring,
      selectSituation,
      buyItem,
      robMerchant,
      currentWeight,
      maxLoad,
      selectedWeight,
      isAutoAttacking,
      autoAttackIntervalMs,
      eventData,
      showFleeOptions,
      isConfirmingGoHome,
      activeTab,
      selectedMerchant,
      selectedMutant,
      merchantStats,
      mutantStats,
      selectedVillager,
      villagerStats,
      isConfirmingRob,
      handleRobClick,
      isConfirmingFight,
      handleFightClick,
      isConfirmingCapture,
      handleCaptureClick,
      canGoHome,
      randomPhrase,
      mutantPhrase,
      villagerPhrase,
      isDanger,
      occupierData,
      isNPC,
      handleChallengeClick,
      handleClearClick,
      isConfirmingChallenge,
      isConfirmingClear,
      isConfirmingBuy,
      selectedItemToBuy,
      handleBuyClick,
      isConfirmingHire,
      handleHireClick,
      getPerkDescription
    };
  },
};
</script>
<style scoped>
/* Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}
/* Animations */
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}
@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}
@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}
.animate-pulse {
  animation: pulse 1.5s infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
.animate-bounce {
  animation: bounce 0.5s infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}
/* Button States */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
/* Responsive */
@media (max-width: 640px) {
  .text-sm {
    font-size: 0.75rem;
  }
  .text-xs {
    font-size: 0.625rem;
  }
  .p-4 {
    padding: 0.75rem;
  }
  .w-16 {
    width: 3rem;
    height: 3rem;
  }
  .max-h-64 {
    max-height: 16rem;
  }
}
/* Fade/slide for item list */
.fade-list-enter-active,
.fade-list-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.fade-list-enter-from,
.fade-list-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
/* Custom text size */
.text-xxs {
  font-size: 10px;
  line-height: 1rem;
}
</style>
